/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable camelcase */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useEditor, EditorContent } from '@tiptap/react';
import LinkExtension from '@tiptap/extension-link';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Paragraph from '@tiptap/extension-paragraph';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Placeholder from '@tiptap/extension-placeholder';
import LinkIcon from '@material-ui/icons/Link';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import LinkOffIcon from '@material-ui/icons/LinkOff';

import { COMMENT_RULES, EDITOR_FOCUSED_CLASS_PREFIX } from 'constants/index';
import isValidURL from 'api/helpers/isValidURL';

import UrlDialog from 'components/UrlDialog';

import { isMac } from 'services/environment';

const MIN_LENGTH = COMMENT_RULES.review.minLength;
const MAX_LENGTH = COMMENT_RULES.review.maxLength;

const VALID_CKBK_URL_ERR_MSG = 'Links must begin with https://app.ckbk.com/';
const VALID_URL_ERR_MSG = 'Please enter a valid URL';
const CKBK_URL_PREFIX = 'https://app.ckbk.com';

const RichTextField = React.forwardRef((props, ref) => {
  const {
    focused,
    handleBlur,
    handleReviewChange,
    isEditingName,
    onFocus,
    onOpen,
    review,
    isOpen,
    isModerator,
    isDirty,
    setReviewLength,
    maxLength,
    minLength,
    placeholder,
    id,
    fullWidth,
  } = props;

  // each open editor should plant their own flag on the body, so they don't mistakenly unflag each-other
  const editorFocusedClass = useMemo(
    () => `${EDITOR_FOCUSED_CLASS_PREFIX}_${Math.floor(Math.random() * 100)}`,
    [],
  );

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [inputUrl, setInputUrl] = useState('');

  const handleOnFocus = useCallback(
    () => {
      if (urlDialogOpen) {
        return;
      }
      // Doing this to disable Swipe-able content when we are writing something
      document.body.classList.add(editorFocusedClass);

      if (!onFocus) {
        return;
      }

      onFocus();
    },
    [urlDialogOpen, onFocus, editorFocusedClass],
  );

  const handleOnBlur = useCallback(
    () => {
      if (urlDialogOpen) {
        return;
      }

      document.body.classList.remove(editorFocusedClass);

      if (!handleBlur) {
        return;
      }

      handleBlur();
    },
    [urlDialogOpen, handleBlur, editorFocusedClass],
  );

  const handleEditorUpdate = ({ editor }) => {
    handleReviewChange(editor.getHTML());
  };

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      Bold,
      Italic,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
    ],
    content: review,
    editable: !isEditingName,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    onUpdate: handleEditorUpdate,
  });

  useEffect(
    () => {
      if (focused) editor.commands.focus();
    },
    [focused],
  );

  const validateURL = url => {
    if (isValidURL(url)) {
      return true;
    }

    toast.error(VALID_URL_ERR_MSG);
    return false;
  };

  const handleUrlDialogClose = () => {
    setUrlDialogOpen(false);
    setInputUrl('');
  };

  const handleUrlDialogConfirm = url => {
    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .run();

      return;
    }

    const isValid = validateURL(url);

    if (!isValid) {
      return;
    }

    if (!url.startsWith(CKBK_URL_PREFIX) && !isModerator) {
      toast.error(VALID_CKBK_URL_ERR_MSG);

      return;
    }
    // update link
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url })
      .run();

    setUrlDialogOpen(false);
  };

  const setLink = useCallback(
    () => {
      if (!editor || !editor.state || !editor.state.selection) {
        return;
      }

      const { empty } = editor.state.selection;

      if (empty && !editor.isActive('link')) {
        toast.error('Please select text to add a link');
        return;
      }

      const previousUrl = editor.getAttributes('link').href;
      setInputUrl(previousUrl || '');
      setUrlDialogOpen(true);
    },
    [editor],
  );

  const handleKeyDown = useCallback(
    event => {
      if ((isMac() ? event.metaKey : event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        event.stopPropagation();
        setLink();
      }
    },
    [setLink],
  );

  const reviewLength = useMemo(
    () => {
      if (!review) {
        return 0;
      }

      const tempElement = document.createElement('div');
      tempElement.innerHTML = review;

      // Use the innerText property to get the text content without HTML tags
      const textContent = tempElement.innerText || tempElement.textContent;

      const { length } = textContent.trim();

      if (setReviewLength) {
        setReviewLength(length);
      }

      return length;
    },
    [review, setReviewLength],
  );

  const helperText = useMemo(
    () => {
      if (!reviewLength) {
        return null;
      }

      return reviewLength < minLength
        ? `Must be at least ${minLength} characters`
        : `${reviewLength}/${maxLength} characters`;
    },
    [reviewLength, maxLength],
  );

  const handleOnClick = () => {
    if (editor) {
      editor.chain().focus();
    }
    if (onOpen) {
      onOpen();
    }
  };

  const showError = useMemo(() => isOpen && isDirty && reviewLength < minLength && !focused, [
    minLength,
    focused,
    isDirty,
    isOpen,
    reviewLength,
  ]);

  /**
   * useEffect to clean up the editorFocused flag if the editor was closed before being unfocused
   * */
  useEffect(
    () => () => {
      document.body.classList.remove(editorFocusedClass);
    },
    [editorFocusedClass],
  );

  return (
    <>
      <div
        className={`comment-form__col comment-form__col__textfield ${
          fullWidth ? 'w-full' : 'margin-top-10'
        }`}
        id={id}
        ref={ref}
        onClick={handleOnClick}
      >
        <div className={`comment-form__toolbar ${isOpen ? 'comment-form__toolbar__focused' : ''}`}>
          <button
            type="button"
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleBold()
                .run()
            }
            className={editor.isActive('bold') ? 'is-active' : 'is-not-active'}
          >
            <FormatBoldIcon fontSize="medium" />
          </button>
          <button
            type="button"
            onClick={() =>
              editor
                .chain()
                .focus()
                .toggleItalic()
                .run()
            }
            className={editor.isActive('italic') ? 'is-active' : 'is-not-active'}
          >
            <FormatItalicIcon fontSize="medium" />
          </button>
          <button
            type="button"
            onClick={setLink}
            className={editor.isActive('link') ? 'is-active' : 'is-not-active'}
          >
            <LinkIcon fontSize="medium" />
          </button>
          <button
            type="button"
            onClick={() =>
              editor
                .chain()
                .focus()
                .unsetLink()
                .run()
            }
            className={editor.isActive('link') ? 'is-active' : 'is-not-active'}
          >
            <LinkOffIcon fontSize="medium" />
          </button>
        </div>
        <EditorContent
          editor={editor}
          className={`w-full ${
            focused && !showError ? 'comment-form__col__textfield__focused' : ''
          } ${showError ? 'comment-form__col__textfield__error' : ''}`}
          onKeyDown={handleKeyDown}
        />
        <p
          className={`comment-form__col__textfield__helper ${
            showError ? 'comment-form__col__textfield__helper__error' : ''
          }`}
        >
          {helperText}
        </p>
      </div>

      <UrlDialog
        placeholder={CKBK_URL_PREFIX}
        proceedText={inputUrl ? 'Edit' : 'Add'}
        title={inputUrl ? 'Edit link' : 'Add link'}
        url={inputUrl}
        onAgree={handleUrlDialogConfirm}
        open={urlDialogOpen}
        onClose={handleUrlDialogClose}
      />
    </>
  );
});

RichTextField.propTypes = {
  review: PropTypes.string.isRequired,
  handleReviewChange: PropTypes.func.isRequired,
  setReviewLength: PropTypes.func,
  onOpen: PropTypes.func,
  fullWidth: PropTypes.bool,
  isEditingName: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  isModerator: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  focused: PropTypes.bool.isRequired,
  handleBlur: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  id: PropTypes.string,
  placeholder: PropTypes.string,
};

RichTextField.defaultProps = {
  maxLength: MAX_LENGTH,
  minLength: MIN_LENGTH,
  id: undefined,
  fullWidth: false,
  isEditingName: false,
  placeholder: 'Write review here...',
  onOpen: () => {},
  setReviewLength: () => {},
};

export default React.memo(RichTextField);
